var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import { SORT_ASC, SORT_DESC } from './constants';
import { columnTypes } from './types';
import TextFilter from './components/filters/TextFilter';
import EnumFilter from './components/filters/EnumFilter';
import NumberFilter from './components/filters/NumberFilter';
import DateFilter from './components/filters/DateFilter';
import TimeFilter from './components/filters/TimeFilter';
import BoolFilter from './components/filters/BoolFilter';
import PercentageFilter from './components/filters/PercentageFilter';
import IconFilter from './components/filters/IconFilter';
import TextCell from './components/cells/TextCell';
import EnumCell from './components/cells/EnumCell';
import DateCell from './components/cells/DateCell';
import TimeCell from './components/cells/TimeCell';
import BoolCell from './components/cells/BoolCell';
import IconCell from './components/cells/IconCell';
import PercentageCell from './components/cells/PercentageCell';
import HyperlinkCell from './components/cells/HyperlinkCell';
import TagCell from './components/cells/TagCell';
import TagFilter from './components/filters/TagFilter';
import AccurityIcon from '../icon/components/AccurityIcon';
export var isHighlightedRow = function (uniqueColumnName, highlightedRows, row) {
    if (!uniqueColumnName) {
        return false;
    }
    if (row[uniqueColumnName]) {
        if (highlightedRows.find(function (s) { return String(row[uniqueColumnName]) === String(s); })) {
            return true;
        }
    }
    return false;
};
export var getFilter = function (column, onFilterChange, customFilters) {
    var type = column.type;
    var component = customFilters && customFilters[type];
    if (component) {
        return React.createElement(component, column);
    }
    switch (type) {
        case columnTypes.TEXT_TYPE:
        case columnTypes.HYPERLINK_TYPE:
        case columnTypes.DOWNLOAD_LINK_TYPE:
            return React.createElement(TextFilter, { textColumn: column, onFilterChange: onFilterChange });
        case columnTypes.PERCENTAGE_TYPE:
            return React.createElement(PercentageFilter, { percentageColumn: column, onFilterChange: onFilterChange });
        case columnTypes.ENUM_TYPE:
            return React.createElement(EnumFilter, { enumColumn: column, onFilterChange: onFilterChange });
        case columnTypes.NUMBER_TYPE:
            return React.createElement(NumberFilter, { numberColumn: column, onFilterChange: onFilterChange });
        case columnTypes.DATE_TYPE:
            return React.createElement(DateFilter, { dateColumn: column, onFilterChange: onFilterChange });
        case columnTypes.TIME_TYPE:
            return React.createElement(TimeFilter, { timeColumn: column, onFilterChange: onFilterChange });
        case columnTypes.BOOL_TYPE:
            return React.createElement(BoolFilter, { boolColumn: column, onFilterChange: onFilterChange });
        case columnTypes.ICON_TYPE:
            return React.createElement(IconFilter, { iconColumn: column, onFilterChange: onFilterChange });
        case columnTypes.TAG_TYPE:
            return React.createElement(TagFilter, { tagColumn: column, onFilterChange: onFilterChange });
        default:
            console.warn("missing filter: " + type);
            return null;
    }
};
/**
 * Creates react node (DOM element) which is in this case a cell in the table based on
 * column type
 *
 * @param column
 * @param value
 */
export var createCell = function (column, value) {
    switch (column.type) {
        case columnTypes.TEXT_TYPE:
        case columnTypes.NUMBER_TYPE:
            return React.createElement(TextCell, { value: value });
        case columnTypes.ENUM_TYPE:
            var enumCellColumn = column;
            return React.createElement(EnumCell, { value: value, options: enumCellColumn.options });
        case columnTypes.DATE_TYPE:
            var format = column.format;
            return React.createElement(DateCell, { value: value, format: format });
        case columnTypes.TIME_TYPE:
            return React.createElement(TimeCell, { value: value });
        case columnTypes.BOOL_TYPE:
            return React.createElement(BoolCell, { value: value });
        case columnTypes.PERCENTAGE_TYPE:
            return React.createElement(PercentageCell, { value: value });
        case columnTypes.HYPERLINK_TYPE:
            return React.createElement(HyperlinkCell, { value: value });
        // case columnTypes.DOWNLOAD_LINK_TYPE:
        //   return React.createElement(DownloadLinkCell, { value, config: config as DownloadColumnCellConfig }); FIXME attach DownloadColumnCellConfig to COLUMN not to each value
        case columnTypes.ICON_TYPE:
            return React.createElement(IconCell, { value: value, graphics: column.graphics });
        case columnTypes.TAG_TYPE:
            return React.createElement(TagCell, { value: value, column: column });
        default:
            console.warn("missing cell type: " + column.type);
            return '';
    }
};
export var createTooltip = function (_a) {
    var value = _a.value, description = _a.description;
    return "<div><strong>" + (value || '') + "</strong><br />" + (description || '') + "</div>";
};
export var filterVisible = function (columns) {
    var result = [];
    columns.forEach(function (c, index) {
        if (!c.hidden) {
            result.push(__assign(__assign({}, c), { index: index }));
        }
    });
    return result;
};
export var getSortType = function (type) {
    switch (type) {
        case SORT_ASC:
            return SORT_DESC;
        case SORT_DESC:
            return undefined;
        default:
            return SORT_ASC;
    }
};
export var getSortIcon = function (type) {
    switch (type) {
        case SORT_ASC:
            return React.createElement(AccurityIcon, { iconName: 'mui:ArrowDropUp', size: 'small', className: 'fleft' });
        case SORT_DESC:
            return React.createElement(AccurityIcon, { iconName: 'mui:ArrowDropDown', size: 'small', className: 'fleft' });
        default:
            return '';
    }
};
/**
 * Get value at path from object.
 * We cannot use lodash `get` because we need to support case like:
 * path:              'foo.bar'
 * object:            { foo: [{ bar: 'red' }, { bar: 'blue' }] }
 * desired result:    ['red', 'blue']
 *
 * @param object Some object from which we want to read a value, eg. `{ foo: 1, nested: { bar: 2, multi: [3,4,5] }}`
 * @param path Path to the value with dot notation eg, `foo` or `nested.multi.2`
 * @returns {any} Returns value if possible to resolve path, or undefined
 */
export var resolvePath = function (object, path) {
    try {
        return recursivelyResolvePath(object, path);
    }
    catch (err) {
        console.error('Could not resolve path ' + path + ' on object ' + JSON.stringify(object) + ' !', err);
        return undefined;
    }
};
var recursivelyResolvePath = function (object, path) {
    if (!object) {
        return undefined;
    }
    var nextDotIndex = path.indexOf('.');
    if (nextDotIndex === -1) {
        return object[path];
    }
    var nextPathPart = path.substring(0, nextDotIndex);
    var remainingPath = path.substring(nextDotIndex + 1);
    var nextObject = object[nextPathPart];
    if (Array.isArray(nextObject)) {
        return nextObject.map(function (element) { return recursivelyResolvePath(element, remainingPath); });
    }
    else {
        return recursivelyResolvePath(nextObject, remainingPath);
    }
};
//# sourceMappingURL=utils.js.map